import { Injectable, Inject } from "@angular/core";
import { HttpClientService } from "./http-client.service";
import { Observable, of } from "rxjs";
import { shareReplay } from "rxjs/operators";
import { map, tap, concatMap, catchError } from "rxjs/operators";
import { Router } from "@angular/router";
import { HttpClient, HttpResponse, HttpParams } from "@angular/common/http";
import { EnvironmentService } from "./environment.service";
import { initializeDynamicTheme, clearDynamicTheme } from "../theme-utilities/dynamic-theme-loader";
import { QUOTES_V2_ACCESS_ACTIVITY, QUOTES_V2_DIRECT_ACCESS_AGREEMENT_CODES } from "../utils/quote.utils";
import { APPROVAL_PROCESS_URL_ACCESS_ACTIVITY } from "../utilities/approval-process-flux.util";
import { UserActivity } from "../interfaces/user-activity.interface";
import { UserWithActivities } from "../interfaces/user-with-activities.interface";

@Injectable()
export class UsersService {
  public token: string;
  public currentUser: any;
  /** Listeners notified on every auth reset (logout, token revocation). */
  private logout_callbacks: Array<() => void> = [];
  private cacheSubordinate: Observable<any>;
  private cacheDealershipsBasicInfo: Observable<any>;
  private cacheQuotesV2Access: Observable<boolean>;
  private cacheApprovalProcessUrlAccess: Observable<boolean>;
  private cacheQuoterV2ForAll: Observable<boolean>;
  private cacheApplicationQuoterV2View: Observable<boolean>;
  private cacheLegacyQuotesV2View: Observable<boolean>;
  private cacheSalesmanCardsAccess: Observable<boolean>;
  static readonly USER_TYPES = {
    DEVELOPER: "developer",
  };
  static readonly SALESMAN_CARDS_ADMIN_ACTIVITY = "salesman_cards_admin";

  constructor(
    private http: HttpClientService,
    private router: Router,
    private httpUnsecure: HttpClient,
    private environmentService: EnvironmentService
  ) {}

  private get endpoint(): string {
    return this.environmentService.get("api.url") || "";
  }

  public getClients({
    view_as,
    page = 1,
    page_size = 20,
    status = undefined,
    person_id = undefined,
    search = undefined,
  }): Observable<any> {
    const params = {
      status,
      "view_as[]": view_as,
      page: page.toString(),
      page_size: page_size.toString(),
      person_id: person_id.toString(),
      search,
    };
    return this.http
      .get(`/api/users/${this.getCurrentUser().id}/clients`, {
        params,
      })
      .pipe(
        tap((_) => console.log("UserService.getClients: Ok.")),
        map((response) => response)
      );
  }

  public getSubordinates(): Observable<any> {
    if (!this.cacheSubordinate) {
      this.cacheSubordinate = this.requestSubordinates().pipe(shareReplay(1));
    }
    return this.cacheSubordinate;
  }

  public getUsersByArea(area): Observable<any> {
    const params = {
      params: {
        area,
      },
    };
    return this.http.get("/api/users/by_area", params).pipe(tap((_) => console.log("UserService.getUsersByArea: Ok")));
  }

  private requestSubordinates(page?: number, perPage?: number, query?: string): Observable<any> {
    const id = this.getCurrentUser().id;
    return this.http
      .get(`/api/users/${id}/subordinates`, {
        params: { id, page: page?.toString(), per_page: perPage?.toString(), query },
      })
      .pipe(tap((_) => console.log("UserService.getSubordinates: Ok.")));
  }

  /**
   * Subordinates split by internal vs external teams.
   * When `scope` is set, the backend should return only that slice (recommended).
   * Params: `scope=internal` | `scope=external` on GET .../subordinates_ext_int
   */
  public getSubordinatesExtInt(
    page?: number,
    per_page?: number,
    query?: string,
    scope?: "internal" | "external"
  ): Observable<any> {
    const id = this.getCurrentUser().id;
    const params: Record<string, string | undefined> = {
      id: String(id),
      query,
      page: page?.toString(),
      per_page: per_page?.toString(),
    };
    if (scope) {
      params["scope"] = scope;
    }
    return this.http
      .get(`/api/users/${id}/subordinates_ext_int`, {
        params,
      })
      .pipe(tap((_) => console.log("UserService.getSubordinatesExtInt: Ok.")));
  }

  public flux(id: number | string, action: string, usuario_id: number | string, options?: any): Observable<any> {
    const params = {
      flux_action: action,
      options: options || "{}",
      usuario_id,
    };
    return this.http
      .post(`/api/quotes/${id}/flux_action`, params)
      .pipe(tap((_) => console.log("UserService.flux: Ok.")));
  }

  public put(id: number | string, options: any): Observable<any> {
    return this.http.put(`/api/quotes/${id}`, options).pipe(tap((_) => console.log("UserService.put: Ok.")));
  }

  public getMarketingClients(): Observable<[]> {
    return this.http
      .get("/api/users/mkt_approval_process")
      .pipe(tap((_) => console.log("UserService.getMarketingClients: Ok.")));
  }

  public searchPerson(key: string): Observable<[]> {
    const params = new HttpParams().set("search", key);
    return this.http.get("/api/persons/search", { params }).pipe(
      tap((_) => console.log("UserService.searchPerson: Ok.")),
      map((value) => value["persons"])
    );
  }

  public getAvailableTime(id: string, step = "30"): Observable<any> {
    const params = new HttpParams().set("activity_id", id).set("step", step);
    return this.http
      .get("/api/users/available_time", { params })
      .pipe(tap((_) => console.log("UserService.getAvailableTime: Ok.")));
  }

  public getAllDealerships(application_id: number | string = undefined): Observable<any> {
    const params = new HttpParams().set("application_id", application_id);
    return this.http
      .get("/api/dealerships", { params })
      .pipe(tap((_) => console.log("UserService.getAllDealerships: Ok.")));
  }

  public getAllDealershipsBasicInfo(): Observable<any> {
    if (!this.cacheDealershipsBasicInfo) {
      this.cacheDealershipsBasicInfo = this.http.get("/api/dealerships/basic_data").pipe(
        tap((_) => console.log("UserService.getAllDealershipsBasicInfo: Ok.")),
        shareReplay(1)
      );
    }
    return this.cacheDealershipsBasicInfo;
  }

  public getAsignedDealerships(id): Observable<any> {
    const options = {
      params: { id },
    };
    return this.http
      .get("/api/dealerships/filter", options)
      .pipe(tap((_) => console.log("UserService.getAsinedDealerships: Ok.")));
  }

  public setDealerships(id, dealership_id, relation): Observable<any> {
    const params = {
      dealership_id,
      relation,
    };
    return this.http
      .post(`/api/users/${id}/add_dealearship`, params)
      .pipe(tap((_) => console.log("UserService.setDealerships: Ok.")));
  }

  public removeDealership(id, dealership_id): Observable<any> {
    const params = {
      dealership_id,
    };
    return this.http
      .post(`/api/users/${id}/remove_dealearship`, params)
      .pipe(tap((_) => console.log("UserService.removeDealership: Ok.")));
  }
  public getUserDealerships(user_id): Observable<any> {
    return this.http
      .get(`/api/users/${user_id}/dealerships/dealerships`)
      .pipe(tap((_) => console.log("UserService.getUserDealerships: Ok.")));
  }

  public getDealershipsAssignableByManager(
    user_id,
    params?: {
      limit?: number;
      search?: string;
      include_base_64_metadata?: boolean;
    }
  ): Observable<any> {
    const httpParams = new HttpParams()
      .set("limit", params?.limit)
      .set("search", params?.search)
      .set("include_base_64_metadata", params?.include_base_64_metadata);

    return this.http
      .get(`/api/users/${user_id}/assignable_by_manager`, { params: httpParams })
      .pipe(tap((_) => console.log("UserService.getDealershipsAssignableByManager: Ok.")));
  }

  public getDealershipsAssignableByUser(
    user_id,
    params?: {
      limit?: number;
      search?: string;
      include_base_64_metadata?: boolean;
    }
  ): Observable<any> {
    const httpParams = new HttpParams()
      .set("limit", params?.limit)
      .set("search", params?.search)
      .set("include_base_64_metadata", params?.include_base_64_metadata);

    return this.http
      .get(`/api/users/${user_id}/assignable_by_user`, { params: httpParams })
      .pipe(tap((_) => console.log("UserService.getDealershipsAssignableByUser: Ok.")));
  }

  public getDealershipsEditableByManager(user_id): Observable<any> {
    return this.http
      .get(`/api/users/${user_id}/editable_by_manager`)
      .pipe(tap((_) => console.log("UserService.getDealershipsEditableByManager: Ok.")));
  }

  public getCoworkers() {
    return this.http.get("/api/users/coworkers").pipe(tap((_) => console.log("UserService.getCoworkers: Ok.")));
  }

  public changeAssigned(id: string | number, owner_id: string | number): Observable<any> {
    const params = {
      id,
      owner_id,
    };
    return this.http
      .post(`/api/users/${id}/assign_manager`, params)
      .pipe(tap((_) => console.log("UserService.changeAssigned: Ok.")));
  }

  public updateActive(id): Observable<any> {
    return this.http
      .put(`/api/users/${id}/update_active`, {})
      .pipe(tap((_) => console.log("UserService.updateActive: Ok.")));
  }

  public notifyHandover(old_id, new_id, client_id, comments?): Observable<any> {
    const params = { id: old_id, new_id, comments, uuid: client_id };
    return this.http
      .put(`/api/users/${old_id}/client_handover_mailer`, params)
      .pipe(tap((_) => console.log("UserService.notifyHandover: Ok.")));
  }

  public resetPassword(id, options): Observable<any> {
    return this.http
      .put(`/api/users/${id}/reset_password`, options)
      .pipe(tap((_) => console.log("UserService.resetPassword: Ok.")));
  }

  login(username: string, password: string): Observable<boolean> {
    username = username.toLowerCase();
    let currentUser: {
      access_token: any;
      refresh_token: any;
      expires_at: number;
    };
    return this.httpUnsecure
      .post(`${this.endpoint}/auth/sign_in.json`, {
        user: {
          email: username,
          password,
        },
      })
      .pipe(
        concatMap((resp: Response): Observable<unknown> => {
          currentUser = resp["data"];
          return this.httpUnsecure.post(`${this.endpoint}/oauth/token`, {
            email: username,
            password,
            grant_type: "password",
            scope: "admin",
          });
        }),
        concatMap((response: any): Observable<boolean> => {
          const token = response && response["access_token"];
          if (!token) return of(false);

          return this.finalizeLogin(
            currentUser,
            token,
            response["refresh_token"],
            response["expires_in"],
            response["created_at"]
          ).pipe(map(() => true));
        })
      );
  }

  loginOtp(phone: string, code: number): Observable<string> {
    let currentUser: {
      access_token: any;
      refresh_token: any;
      expires_at: number;
    } = { access_token: null, refresh_token: null, expires_at: 0 };

    return this.httpUnsecure
      .post(`${this.endpoint}/login_otp`, {
        phone,
        code,
      })
      .pipe(
        concatMap((response: any): Observable<string> => {
          const token = response && response["access_token"];
          if (!token) {
            return of(response["status"] || "error");
          }

          currentUser = response["user"];
          return this.finalizeLogin(
            currentUser,
            token,
            response["refresh_token"],
            response["expires_in"],
            response["created_at"]
          ).pipe(map(() => "success"));
        })
      );
  }

  sendOtpCode(phone: string): Observable<any> {
    return this.httpUnsecure
      .post(`${this.endpoint}/send_code`, {
        phone,
      })
      .pipe(map((response) => response));
  }

  refreshToken(): void {
    if (!this.isAuthenticated) {
      this.logout();
      this.router.navigate(["/login"]);
    } else {
      this.http
        .post("/oauth/token", {
          refresh_token: this.getCurrentUser()?.refresh_token,
          grant_type: "refresh_token",
        })
        .subscribe(
          (response) => {
            const token = response && response["access_token"];
            if (token) {
              const currentUser = this.getCurrentUser();
              this.token = token;
              currentUser.access_token = token;
              currentUser.refresh_token = response["refresh_token"];
              currentUser.expires_at = Number(response["expires_in"]) + Number(response["created_at"]);
              localStorage.setItem("currentUser", JSON.stringify(currentUser));
            } else {
              this.logout();
              this.router.navigate(["/login"]);
            }
          },
          () => {
            this.logout();
            this.router.navigate(["/login"]);
          }
        );
    }
  }

  public logout(): void {
    this.http
      .post("/oauth/revoke", {
        token: this.token,
      })
      .subscribe((response) => {
        this.resetAuthData();
      });
  }

  public revokeAllTokens(): void {
    this.http.delete("/revoke_all").subscribe(() => {
      this.resetAuthData();
    });
  }

  public getAutoscoreAccessStatus(current_user_id: number): Observable<any> {
    return this.http
      .get(`/api/users/${current_user_id}/autoscore_access_status`)
      .pipe(tap((_) => console.log("UsersService.getAutoscoreAccessStatus: Ok")));
  }

  public revokeAllIndexTokens(app_name: string, route_path = "/login"): void {
    this.http.delete("/revoke_all_index", { params: { application_name: app_name } }).subscribe(() => {
      this.resetAuthData(route_path);
    });
  }

  isSessionValid(app_name: string): boolean {
    let is_valid = true;

    if (this.isAuthenticated) {
      this.revokeAllIndexTokens(app_name);
      is_valid = false;
    }

    return is_valid;
  }

  /** Registers a callback invoked on every auth reset (e.g. analytics identity cleanup). */
  registerLogoutCallback(callback: () => void): void {
    this.logout_callbacks.push(callback);
  }

  resetAuthData(route_path = "/login"): void {
    for (const callback of this.logout_callbacks) {
      try {
        callback();
      } catch {
        // A failing listener must never block the logout itself.
      }
    }
    this.token = null;
    localStorage.removeItem("currentUser");
    localStorage.removeItem("access_token");
    // Clear dynamic theme context on logout
    localStorage.removeItem("session_context");
    try {
      if (typeof clearDynamicTheme !== "undefined") {
        clearDynamicTheme();
      }
    } catch {
      // Ignore theme cleanup failures (e.g. unavailable in some environments)
    }
    const currentUser = this.getCurrentUser();
    this.currentUser = null;
    this.cacheQuotesV2Access = null;
    this.cacheApprovalProcessUrlAccess = null;
    this.cacheSalesmanCardsAccess = null;
    this.router.navigate([route_path]);
  }

  public isAuthenticated(): boolean {
    return !!(this.token || this.getCurrentUser()?.access_token);
  }

  reloadCurrentUser(): void {
    this.http
      .get(`/api/users/${this.getCurrentUser().id}`)
      .pipe(tap((_) => console.log("UserService.reloadCurrentUser: Ok.")))
      .subscribe((user) => {
        this.currentUser = { ...this.getCurrentUser(), ...user };
        localStorage.setItem("currentUser", JSON.stringify(this.currentUser));
        this.invalidateApprovalProcessUrlAccessCache();
      });
  }
  getCurrentUser() {
    return this.currentUser || JSON.parse(localStorage.getItem("currentUser"));
  }

  getCurrentToken(): string {
    this.refreshToken();
    return this.token;
  }

  public createSalesperson(id, user): Observable<any> {
    return this.http.post(`/api/users/${id}/manage`, { user }).pipe(map((response) => response));
  }

  public manageDuplicatedUser(id, user, is_same_user: boolean): Observable<any> {
    return this.http
      .post(`/api/users/${id}/manage_duplicated`, {
        user,
        is_same_user,
      })
      .pipe(tap((_) => console.log("UserService.manageDuplicatedUser: Ok.")));
  }

  public getUsers(
    filter = "",
    sort_by = "created_at",
    sort_order = "asc",
    page_number = "1",
    page_size = "10",
    view_as = ""
  ): Observable<any> {
    const options = {
      params: {
        filter,
        sort_by,
        sort_order,
        page_number,
        page_size,
        view_as,
      },
    };

    return this.http.get("/api/users/users_with_managers", options).pipe(
      tap((_) => console.log("UsersService.usersWithManagers: Ok.")),
      map((response: HttpResponse<object>) => response)
    );
  }

  public getContactsByUser(from, to, filter?): Observable<any> {
    const options = {
      params: {
        from,
        to,
        filter,
      },
    };

    return this.http.get("/api/users/contacts_by_user", options).pipe(
      tap((_) => console.log("UsersService.contacts_by_user: Ok.")),
      map((response: HttpResponse<object>) => response)
    );
  }

  public removeManager(managerEmail, usuarioId): Observable<any> {
    return this.http
      .post(`/api/users/${usuarioId}/remove_manager`, { manager_email: managerEmail })
      .pipe(tap((_) => console.log("UserService.removeManager: Ok.")));
  }

  public addManager(managerEmail, usuarioId): Observable<any> {
    return this.http
      .post(`/api/users/${usuarioId}/add_manager`, { manager_email: managerEmail })
      .pipe(tap((_) => console.log("UserService.addManager: Ok.")));
  }

  public getIndexWithManagers(): Observable<any[]> {
    return this.http
      .get("/api/users/index_with_managers")
      .pipe(tap((_) => console.log("UserService.getIndexWithManagers: Ok.")));
  }

  public resetPasswordMailer(email, url): Observable<any> {
    const params = {
      email,
      site: url,
    };
    return this.httpUnsecure
      .post(`${this.endpoint}/api/users/reset_password_email`, params)
      .pipe(tap((_) => console.log("UsersService.resetPasswordMailer: Ok")));
  }

  public updatePassword(token, password, password_confirmation): Observable<any> {
    const params = {
      reset_password_token: token,
      password,
      password_confirmation,
    };
    return this.httpUnsecure
      .post(`${this.endpoint}/api/users/update_password`, params)
      .pipe(tap((_) => console.log("UsersService.reset_password_mailer: Ok")));
  }

  public getAllUsers({ tipo = undefined, internal = undefined, like = undefined }): Observable<any> {
    const options = {
      params: { tipo, internal, like },
    };
    return this.http.get("/api/users", options).pipe(
      tap((_) => console.log("UsersService.getAllUsers: Ok.")),
      map((response: HttpResponse<object>) => response)
    );
  }

  public getUsersByRelation({ relation = undefined, like = undefined }): Observable<any> {
    const options = {
      params: { relation, like },
    };
    return this.http.get("/api/users/users_by_relation", options).pipe(
      tap((_) => console.log("UsersService.getAllUsers: Ok.")),
      map((response: HttpResponse<object>) => response)
    );
  }

  public putUser(user_id: number | string, user) {
    return this.http
      .put(`/api/users/${user_id}`, { user })
      .pipe(tap((_) => console.log("UsersService.putUpdateUser: Ok")));
  }

  public getUserWithoutTeam() {
    return this.http
      .get("/api/users/without_team")
      .pipe(tap((_) => console.log("UsersService.getUserWithoutTeam: Ok")));
  }

  public getManagersList() {
    return this.http.get("/api/users/managers_list").pipe(tap((_) => console.log("UsersService.getManagersList: Ok")));
  }

  public getManagers(id = null) {
    let options = {};
    if (id) {
      options = {
        params: {
          id,
        },
      };
    }
    return this.http.get("/api/users/managers", options).pipe(tap((_) => console.log("UsersService.getManagers: Ok")));
  }

  /**
   * Returns only managers valid for legal representative (digital_signature present and active).
   */
  public getManagersValidForLegalRepresentative(): Observable<any> {
    return this.http
      .get("/api/users/managers", {
        params: { valid_for_legal_representative: "true" },
      })
      .pipe(tap((_) => console.log("UsersService.getManagersValidForLegalRepresentative: Ok")));
  }

  public getValidEmailVendor(email): Observable<any> {
    const options = {
      params: {
        email,
      },
    };
    return this.http
      .get("/api/users/validate_email_vendor", options)
      .pipe(tap((_) => console.log("UsersService.getValidEmailVendor")));
  }

  public getValidPhoneVendor(phone): Observable<any> {
    const options = {
      params: {
        phone,
      },
    };
    return this.http
      .get("/api/users/validate_phone_vendor", options)
      .pipe(tap((_) => console.log("UsersService.getValidPhoneVendor")));
  }

  public getUserByCurp(curp: string): Observable<any> {
    const options = {
      params: {
        curp,
      },
    };
    return this.http.get("/api/users/by_curp", options).pipe(tap((_) => console.log("UsersService.getUserByCurp")));
  }

  public getManagerDocuments(id): Observable<any> {
    return this.http
      .get(`/api/users/${id}/manager_documents`)
      .pipe(tap((_) => console.log("UsersService.getManagerDocuments")));
  }

  public getExternalUserData(id): Observable<any> {
    return this.http
      .get(`/api/users/${id}/external_user_data`)
      .pipe(tap((_) => console.log("UsersService.getExternalUserData")));
  }

  public getUserById(user_id): Observable<any> {
    const options = { params: { user_id } };
    return this.http.get(`/api/users/${user_id}`, options).pipe(tap((_) => console.log("UsersService.getUserById")));
  }

  public getClientApplications(id: number, delivered = true, page_size: number, page_number: number): Observable<any> {
    const options = {
      params: {
        delivered: delivered.toString(),
        page_size: page_size.toString(),
        page_number: page_number.toString(),
      },
    };
    return this.http
      .get(`/api/users/${id}/clients_applications`, options)
      .pipe(tap((_) => console.log("UsersService.getClientApplications")));
  }

  public setNotificationStatus(id: number, enabled: boolean) {
    const params = { enabled };
    return this.http
      .put(`/api/users/${id}/set_notification_status`, params)
      .pipe(tap((_) => console.log("UserService.setNotificationStatus: Ok.")));
  }

  public getDealershipListByEP(id: string | number): Observable<any> {
    return this.http
      .get(`/api/users/${id}/list_dealerships`)
      .pipe(tap((_) => console.log("UserService.getDealershipListByEP: Ok.")));
  }

  public getEpsByManager(id: string | number): Observable<any> {
    return this.http
      .get(`/api/users/${id}/list_by_manager`)
      .pipe(tap((_) => console.log("UserService.getEpsByManager: Ok.")));
  }

  public loginNexuIndex(email: string = null, password: string = null): Observable<any> {
    const credentials = { email, password, method: "autoscore" };
    return this.httpUnsecure.post(`${this.endpoint}/signin_index`, credentials).pipe(
      concatMap((response: any): Observable<boolean> => {
        const token = response?.token?.access_token;
        if (!token) return of(false);

        const current_user = { ...response?.user };
        return this.finalizeLogin(current_user, token).pipe(map(() => true));
      })
    );
  }

  public getUser(id = null) {
    const options = { params: { id } };
    return this.http.get(`/api/users/${id}`, options).pipe(tap((_) => console.log("UsersService.getUser: Ok")));
  }

  public getUsersByEmailPhone(email: string, phone: string) {
    const options = { params: { email, phone } };
    return this.http
      .get("/api/users/by_email_phone", options)
      .pipe(tap((_) => console.log("UsersService.getUsersByEmailPhone: Ok")));
  }

  public combineUser(current_user_id, duplicate_user_id, user): Observable<any> {
    return this.http
      .put(`/api/users/${current_user_id}/combine_users`, { user, duplicate_user_id })
      .pipe(tap((_) => console.log("UsersService.combineUser: Ok")));
  }

  public createUser(user_data) {
    const options = { user_data };
    return this.http.post("/api/users/", options).pipe(tap((_) => console.log("UsersService.createUser: Ok")));
  }

  public addUserActivity(user_id: number, activity_name: string): Observable<any> {
    const options = {
      user_id,
      activity_name,
    };
    return this.http
      .post("/api/users_activities/add_user_activity", options)
      .pipe(tap((_) => console.log("UsersService.addUserActivity: Ok")));
  }

  public removeUserActivity(user_activity_id: any): Observable<any> {
    const options = {
      user_activity_id,
    };
    return this.http
      .post("/api/users_activities/remove_user_activity", options)
      .pipe(tap(() => console.log("UsersService.removeUserActivity: Ok")));
  }
  public getUsersWithActivities(tipo: string): Observable<UserWithActivities[]> {
    const options = {
      params: { tipo },
    };
    return this.http.get<UserWithActivities[]>("/api/users/users_with_activities", options);
  }

  /**
   * Useful to check if the current_user belongs to a certain activity.
   * @param activity_name - The name of the activity
   * @throws `NotFound` if the current_user does not belong to the activity
   * @returns A `UsersActivity` record
   */
  public getCurrentUserActivity(activity_name: string): Observable<{ user_activity: UserActivity | null }> {
    const options = { params: { activity_name } };
    return this.http
      .get("/api/users_activities/current_user_activity", options)
      .pipe(tap((_) => console.log("UsersService.getCurrentUserActivity: Ok")));
  }

  /**
   * Returns the agreement (alliance) the current user belongs to.
   */
  public getCurrentAgreement(): Observable<{ agreement_code: string | null }> {
    return this.http
      .get<{ agreement_code: string | null }>("/api/users/current_agreement")
      .pipe(tap((_) => console.log("UsersService.getCurrentAgreement: Ok.")));
  }

  /**
   * Resolves (and caches) whether the current user can access QuotesV2.
   * Access is granted when the user is a developer, belongs to an alliance with
   * direct access, or holds the QuotesV2 activity. Any failure falls back to `false` (v1).
   */
  public isUserAllowedQuotesV2(): Observable<boolean> {
    if (!this.cacheQuotesV2Access) {
      this.cacheQuotesV2Access = this.resolveQuotesV2Access().pipe(shareReplay(1));
    }
    return this.cacheQuotesV2Access;
  }

  /**
   * Global kill-switch (backed by a backend env/config var, e.g. Heroku `QUOTER_V2_FOR_ALL`).
   * When enabled, every user gets QuotesV2 (new-quote flow) and the application screen renders
   * `applications-v2`. When disabled, the existing per-user access logic applies. Cached and
   * defaults to `false` on any failure so the app falls back to the legacy behavior.
   */
  public isQuoterV2ActivatedForAll(): Observable<boolean> {
    if (!this.cacheQuoterV2ForAll) {
      this.cacheQuoterV2ForAll = this.resolveQuoterV2ForAll().pipe(shareReplay(1));
    }
    return this.cacheQuoterV2ForAll;
  }

  private resolveQuoterV2ForAll(): Observable<boolean> {
    return of(this.environmentService.getFlag("quoter_v2_for_all"));
  }

  /**
   * Independent kill-switch for the application "Cotizador" tab view. When enabled, applications
   * whose saved quote uses the v2 engine render the new `applications-v2` view; when disabled, the
   * tab always renders the legacy `applications` view. It is decoupled from `quoter_v2_for_all`
   * (which controls v2 quote creation access) so the view and the creation flow can be rolled out or
   * rolled back independently. Cached and defaults to `false` on any failure.
   */
  public isApplicationQuoterV2ViewActivated(): Observable<boolean> {
    if (!this.cacheApplicationQuoterV2View) {
      this.cacheApplicationQuoterV2View = this.resolveApplicationQuoterV2View().pipe(shareReplay(1));
    }
    return this.cacheApplicationQuoterV2View;
  }

  private resolveApplicationQuoterV2View(): Observable<boolean> {
    return of(this.environmentService.getFlag("application_quoter_v2_view"));
  }

  /** Forces existing quote navigation to V2; off preserves engine/access routing. */
  public isLegacyQuotesV2ViewActivated(): Observable<boolean> {
    if (!this.cacheLegacyQuotesV2View) {
      this.cacheLegacyQuotesV2View = this.resolveLegacyQuotesV2View().pipe(shareReplay(1));
    }
    return this.cacheLegacyQuotesV2View;
  }

  private resolveLegacyQuotesV2View(): Observable<boolean> {
    return of(this.environmentService.getFlag("legacy_quotes_v2_view"));
  }

  private resolveQuotesV2Access(): Observable<boolean> {
    // The global flag forces QuotesV2 for everyone; otherwise fall back to per-user access.
    return this.isQuoterV2ActivatedForAll().pipe(
      concatMap((activatedForAll) => {
        if (activatedForAll) {
          return of(true);
        }
        if (this.isCurrentUserDeveloper()) {
          return of(true);
        }
        return this.hasDirectAgreementAccess().pipe(
          concatMap((hasAccess) => (hasAccess ? of(true) : this.hasQuotesV2Activity()))
        );
      })
    );
  }

  private isCurrentUserDeveloper(): boolean {
    return this.getCurrentUser()?.tipo === UsersService.USER_TYPES.DEVELOPER;
  }

  private hasDirectAgreementAccess(): Observable<boolean> {
    return this.getCurrentAgreement().pipe(
      map((agreement) => QUOTES_V2_DIRECT_ACCESS_AGREEMENT_CODES.includes(agreement?.agreement_code ?? "")),
      catchError(() => of(false))
    );
  }

  private hasQuotesV2Activity(): Observable<boolean> {
    return this.getCurrentUserActivity(QUOTES_V2_ACCESS_ACTIVITY).pipe(
      map(() => true),
      catchError(() => of(false))
    );
  }

  /**
   * Resolves (and caches) whether the current user can access the approval-process URL flow
   * (/approval-process/application/:id). Access is granted when the user is a developer, an
   * analyst, or holds the approval_process_url_access activity. Any failure falls back to
   * `false`.
   */
  public isUserAllowedApprovalProcessUrl(): Observable<boolean> {
    if (!this.cacheApprovalProcessUrlAccess) {
      this.cacheApprovalProcessUrlAccess = this.resolveApprovalProcessUrlAccess().pipe(shareReplay(1));
    }
    return this.cacheApprovalProcessUrlAccess;
  }

  /**
   * Forces the next `isUserAllowedApprovalProcessUrl()` call to re-check the backend instead of
   * reusing the cached result. Call this after any action that may have changed the current
   * user's approval_process_url_access activity mid-session (e.g. an activities toggle applied
   * to themselves, or a profile reload).
   */
  public invalidateApprovalProcessUrlAccessCache(): void {
    this.cacheApprovalProcessUrlAccess = null;
  }

  private resolveApprovalProcessUrlAccess(): Observable<boolean> {
    // Full rollout: every analyst can enter the URL flow without the pilot activity gate.
    // Developers keep the bypass; other roles may still enter via approval_process_url_access.
    if (this.isCurrentUserDeveloper() || this.isCurrentUserAnalyst()) {
      return of(true);
    }
    return this.hasApprovalProcessUrlActivity();
  }

  private isCurrentUserAnalyst(): boolean {
    return this.getCurrentUser()?.tipo === "analisis";
  }

  private hasApprovalProcessUrlActivity(): Observable<boolean> {
    return this.getCurrentUserActivity(APPROVAL_PROCESS_URL_ACCESS_ACTIVITY).pipe(
      map(() => true),
      catchError(() => of(false))
    );
  }

  /**
   * Resolves (and caches) whether the current user can access the salesman-cards panel.
   * Access is granted when the user is a developer or holds the salesman_cards_admin activity.
   * Any failure falls back to `false`.
   */
  public isUserAllowedSalesmanCards(): Observable<boolean> {
    if (!this.cacheSalesmanCardsAccess) {
      this.cacheSalesmanCardsAccess = this.resolveSalesmanCardsAccess().pipe(shareReplay(1));
    }
    return this.cacheSalesmanCardsAccess;
  }

  private resolveSalesmanCardsAccess(): Observable<boolean> {
    if (this.isCurrentUserDeveloper()) {
      return of(true);
    }
    return this.getCurrentUserActivity(UsersService.SALESMAN_CARDS_ADMIN_ACTIVITY).pipe(
      map((res) => !!res?.user_activity),
      catchError(() => of(false))
    );
  }

  public getUsersByPhone(phone: string): Observable<any> {
    const options = { params: { phone } };
    return this.http
      .get("/api/users/by_phone", options)
      .pipe(tap((_) => console.log("UsersService.getUsersByPhone: Ok")));
  }

  public deactivateUser(user_id: number | string): Observable<any> {
    return this.http
      .put(`/api/users/${user_id}/deactivate_user`, {})
      .pipe(tap((_) => console.log("UsersService.deactivateUser: Ok")));
  }

  public assignUserToDealership(
    user_id: number | string,
    dealership_id: number | string,
    relation: string
  ): Observable<any> {
    return this.http
      .post(`/api/users/${user_id}/assign_to_dealership`, { dealership_id, relation })
      .pipe(tap((_) => console.log("UsersService.assignUserToDealership: Ok")));
  }

  public getSessionContext(): Observable<any> {
    return this.http.get("/session_context").pipe(
      tap((sessionContext) => {
        if (sessionContext && Object.keys(sessionContext).length > 0) {
          localStorage.setItem("session_context", JSON.stringify(sessionContext));
        } else {
          localStorage.removeItem("session_context");
        }
        // Apply or clear dynamic theme immediately after updating storage
        try {
          if (typeof initializeDynamicTheme !== "undefined") {
            initializeDynamicTheme();
          }
        } catch (e) {
          console.warn("initializeDynamicTheme error", e);
        }
        console.log("UsersService.getSessionContext: Ok.");
      })
    );
  }

  private finalizeLogin(
    currentUser: any,
    token: string,
    refresh_token?: string,
    expires_in?: any,
    created_at?: any
  ): Observable<void> {
    this.token = token;
    currentUser.access_token = token;
    if (refresh_token) currentUser.refresh_token = refresh_token;
    if (expires_in !== undefined && created_at !== undefined) {
      currentUser.expires_at = Number(expires_in) + Number(created_at);
    }
    localStorage.setItem("currentUser", JSON.stringify(currentUser));
    localStorage.setItem("access_token", token);
    this.currentUser = currentUser;

    // Session context is optional - login succeeds even if it fails
    return this.getSessionContext().pipe(
      catchError((err) => {
        console.warn("UsersService.getSessionContext failed during login", err);
        return of(null);
      }),
      map(() => undefined)
    );
  }

  // GET /api/v1/users/:id/check_manager?manager_id=X
  public checkManager(userId: number | string, managerId: number | string): Observable<boolean> {
    return this.http
      .get<{ is_manager: boolean }>(`/api/users/${userId}/check_manager`, {
        params: { manager_id: String(managerId) },
      })
      .pipe(
        map((res) => !!res?.is_manager),
        catchError(() => of(false)),
        tap((_) => console.log("UsersService.checkManager: Ok."))
      );
  }

  // GET /api/users/:id/can_see_ringover_calls
  public getCanSeeRingOverCalls(): Observable<boolean> {
    return this.http
      .get<{ can_see_ringover_calls: boolean }>(`/api/users/${this.getCurrentUser().id}/can_see_ringover_calls`)
      .pipe(
        map((res) => !!res?.can_see_ringover_calls),
        tap((_) => console.log("UsersService.getCanSeeRingOverCalls: Ok."))
      );
  }
}
